import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import { useChat } from '../../contexts/ChatContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useChatPopup } from '../../contexts/ChatPopupContext';
import ChatConversationList from './ChatConversationList';
import ChatRoom from './ChatRoom';
import { supabase } from '../../lib/supabase';
import { chatService } from '../../services/chatService';
import { useToast } from '../../contexts/ToastContext';
import { ConversaItem, chaveDeDireta, chaveDeProjeto, montarConversas } from './conversationList';
import NovaConversaDireta from './NovaConversaDireta';
import type { PessoaDoChat } from '../../services/chatService';
import type { Project, RoomSummary } from './types';

// Cache em memória (por organização) da lista de projetos + resumo das salas
// do chat. O popup do chat desmonta/remonta a cada vez que é fechado e
// reaberto, e sem isso ele refazia a busca inteira toda vez, mesmo sem nada
// ter mudado — daí a sensação de "demora uma eternidade" pra abrir. Só some
// com reload da página.
const projectsCache = new Map<string, Project[]>();
const roomSummaryCache = new Map<string, RoomSummary[]>();

const buscarResumoDasSalas = async (organizationId: string): Promise<RoomSummary[]> => {
  const { data, error } = await supabase.rpc('get_chat_rooms_summary', {
    p_organization_id: organizationId,
  });
  if (error) {
    console.error('Erro ao buscar resumo das salas de chat:', error);
    throw error;
  }
  return (data || []) as RoomSummary[];
};

interface ChatInterfaceProps {
  /** Desktop mostra lista e conversa lado a lado; mobile empilha. */
  duasColunas: boolean;
  larguraDaLista: number;
  listaColapsada: boolean;
  onRedimensionarLista: (largura: number) => void;
  onAlternarLista: () => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  duasColunas,
  larguraDaLista,
  listaColapsada,
  onRedimensionarLista,
  onAlternarLista,
}) => {
  const { organization } = useOrganization();
  const { createRoom, selectRoom, currentRoom, clearCurrentRoom } = useChat();
  const { user } = useAuth();
  const { unreadByRoom, lastMessageByRoom, clearRoomUnread } = useChatPopup();
  const cachedProjects = organization?.id ? projectsCache.get(organization.id) : undefined;
  const [projects, setProjects] = useState<Project[]>(cachedProjects ?? []);
  const [resumos, setResumos] = useState<RoomSummary[]>(
    (organization?.id && roomSummaryCache.get(organization.id)) || []
  );
  const [loading, setLoading] = useState(!cachedProjects);
  const [termo, setTermo] = useState('');
  const [chaveAtiva, setChaveAtiva] = useState<string | null>(null);
  const [escolhendoPessoa, setEscolhendoPessoa] = useState(false);
  const splitRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  useEffect(() => {
    const fetchProjects = async () => {
      if (!organization?.id) {
        console.log('Aguardando ID da organização...');
        return;
      }

      // Já em cache: usa direto, sem esperar rede.
      const cached = projectsCache.get(organization.id);
      if (cached) {
        setProjects(cached);
        setResumos(roomSummaryCache.get(organization.id) || []);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('Buscando projetos para organização:', organization.id);

        // Lista de projetos e resumo das salas de chat (última mensagem +
        // participantes de todas as salas de uma vez) não dependem uma da
        // outra — buscar em paralelo.
        const [{ data, error }, summaryRows] = await Promise.all([
          supabase
            .from('projects')
            .select('*')
            .eq('organization_id', organization.id)
            .eq('status', 'active'),
          buscarResumoDasSalas(organization.id).catch(() => [] as RoomSummary[])
        ]);

        if (error) {
          console.error('Erro ao buscar projetos:', error);
          throw error;
        }

        roomSummaryCache.set(organization.id, summaryRows);
        setResumos(summaryRows);

        if (data) {
          const formattedProjects = data.map(project => ({
            id: project.id,
            name: project.name,
            type: project.type || 'default',
            description: project.description
          }));
          console.log('Projetos encontrados:', formattedProjects);
          projectsCache.set(organization.id, formattedProjects);
          setProjects(formattedProjects);
        }
      } catch (err) {
        console.error('Erro ao buscar projetos:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [organization?.id]);

  // Sala aberta por fora da lista (notificação, toast) também precisa marcar
  // o item correspondente como ativo.
  useEffect(() => {
    if (currentRoom?.type === 'direct') {
      setChaveAtiva(chaveDeDireta(currentRoom.id));
    } else if (currentRoom?.project_id) {
      setChaveAtiva(chaveDeProjeto(currentRoom.project_id));
    } else if (!currentRoom) {
      setChaveAtiva(null);
    }
  }, [currentRoom]);

  const recarregarResumo = useCallback(async () => {
    if (!organization?.id) return;
    try {
      const linhas = await buscarResumoDasSalas(organization.id);
      roomSummaryCache.set(organization.id, linhas);
      setResumos(linhas);
    } catch {
      // Já registrado em buscarResumoDasSalas; a lista segue com o que tinha.
    }
  }, [organization?.id]);

  const { resumoPorProjeto, salasDiretas } = useMemo(() => {
    const porProjeto: Record<string, RoomSummary> = {};
    const diretas: RoomSummary[] = [];
    resumos.forEach(linha => {
      if (linha.room_type === 'direct') {
        diretas.push(linha);
      } else if (linha.project_id) {
        porProjeto[linha.project_id] = linha;
      }
    });
    return { resumoPorProjeto: porProjeto, salasDiretas: diretas };
  }, [resumos]);

  // Conversa privada que alguém acabou de abrir comigo chega como mensagem de
  // uma sala que o resumo (em cache) não conhece. Recarrega o resumo uma vez
  // por sala desconhecida, para ela aparecer na lista.
  const salasJaProcuradasRef = useRef(new Set<string>());
  useEffect(() => {
    if (loading) return;
    const conhecidas = new Set(resumos.map(r => r.room_id));
    const novas = [...Object.keys(unreadByRoom), ...Object.keys(lastMessageByRoom)].filter(
      id => !conhecidas.has(id) && !salasJaProcuradasRef.current.has(id)
    );
    if (novas.length === 0) return;
    novas.forEach(id => salasJaProcuradasRef.current.add(id));
    void recarregarResumo();
  }, [loading, unreadByRoom, lastMessageByRoom, resumos, recarregarResumo]);

  const grupos = useMemo(
    () =>
      montarConversas({
        projetos: projects,
        resumoPorProjeto,
        salasDiretas,
        meuAuthId: user?.id ?? null,
        naoLidasPorSala: unreadByRoom,
        ultimasPorSala: lastMessageByRoom,
        termo,
      }),
    [projects, resumoPorProjeto, salasDiretas, user?.id, unreadByRoom, lastMessageByRoom, termo]
  );

  const handleProjectSelect = async (projectId: string) => {
    if (!organization?.id) {
      console.error('ID da organização não disponível');
      return;
    }

    if (!user?.id) {
      console.error('Usuário não autenticado');
      return;
    }

    try {
      console.log('Selecionando projeto:', projectId);

      // Primeiro, tenta encontrar uma sala existente para o projeto
      const { data: existingRoom, error: roomError } = await supabase
        .from('chat_rooms')
        .select('id')
        .eq('project_id', projectId)
        .eq('organization_id', organization.id)
        .single();

      if (roomError && roomError.code !== 'PGRST116') {
        console.error('Erro ao buscar sala existente:', roomError);
        throw roomError;
      }

      let roomId;

      if (existingRoom) {
        console.log('Sala existente encontrada:', existingRoom.id);
        roomId = existingRoom.id;

        // Verificar se o usuário já é membro
        const { data: memberData, error: memberError } = await supabase
          .from('chat_room_members')
          .select('role')
          .eq('room_id', roomId)
          .eq('user_auth_id', user.id)
          .single();

        // Se não for membro, adicionar como membro
        if (!memberData && (!memberError || memberError.code === 'PGRST116')) {
          console.log('Adicionando usuário como membro da sala existente');
          await chatService.addChatRoomMember({
            room_id: roomId,
            user_auth_id: user.id,
            role: 'member'
          });
        } else if (memberError && memberError.code !== 'PGRST116') {
          console.error('Erro ao verificar membro:', memberError);
          throw memberError;
        }
      } else {
        // Se não existir, cria uma nova sala
        const project = projects.find(p => p.id === projectId);
        if (!project) {
          throw new Error('Projeto não encontrado');
        }

        console.log('Criando nova sala para o projeto:', project.name);
        const newRoom = await createRoom(
          `Chat do Projeto: ${project.name}`,
          'project',
          projectId
        );

        if (!newRoom) {
          throw new Error('Falha ao criar sala de chat');
        }

        roomId = newRoom.id;
        console.log('Nova sala criada:', roomId);
      }

      console.log('Selecionando sala:', roomId);
      await selectRoom(roomId);
      clearRoomUnread(roomId);
      console.log('Sala selecionada com sucesso');
    } catch (err) {
      console.error('Erro ao selecionar projeto:', err);
      toast.error('Erro ao acessar o chat do projeto', 'Por favor, tente novamente.');
      voltarDestaqueParaSalaAtual();
    }
  };

  const voltarDestaqueParaSalaAtual = () => {
    if (currentRoom?.type === 'direct') setChaveAtiva(chaveDeDireta(currentRoom.id));
    else setChaveAtiva(currentRoom?.project_id ? chaveDeProjeto(currentRoom.project_id) : null);
  };

  const abrirSalaDireta = async (roomId: string) => {
    try {
      await selectRoom(roomId);
      clearRoomUnread(roomId);
    } catch (err) {
      console.error('Erro ao abrir conversa privada:', err);
      toast.error('Erro ao abrir a conversa', 'Por favor, tente novamente.');
      voltarDestaqueParaSalaAtual();
    }
  };

  const handleSelecionar = (conversa: ConversaItem) => {
    // Destaca o item na hora: a sala pode levar um instante para carregar (ou
    // até precisar ser criada) e a lista não deve parecer travada.
    setChaveAtiva(conversa.chave);
    if (conversa.roomId) clearRoomUnread(conversa.roomId);
    if (conversa.tipo === 'direta' && conversa.roomId) {
      void abrirSalaDireta(conversa.roomId);
    } else if (conversa.projectId) {
      handleProjectSelect(conversa.projectId);
    }
  };

  const handleEscolherPessoa = async (pessoa: PessoaDoChat) => {
    if (!organization?.id) return;
    setEscolhendoPessoa(false);
    setTermo('');
    try {
      const roomId = await chatService.getOrCreateDirectChat(organization.id, pessoa.authId);
      setChaveAtiva(chaveDeDireta(roomId));
      // A sala pode ter acabado de nascer: sem recarregar, ela não aparece na lista.
      await Promise.all([recarregarResumo(), selectRoom(roomId)]);
      clearRoomUnread(roomId);
    } catch (err) {
      console.error('Erro ao iniciar conversa privada:', err);
      toast.error(`Não foi possível conversar com ${pessoa.nome}`, 'Por favor, tente novamente.');
      voltarDestaqueParaSalaAtual();
    }
  };

  const iniciarResizeDaLista = (e: React.MouseEvent) => {
    e.preventDefault();
    const container = splitRef.current;
    if (!container) return;

    const esquerda = container.getBoundingClientRect().left;
    const mover = (ev: MouseEvent) => onRedimensionarLista(ev.clientX - esquerda);
    const parar = () => {
      document.removeEventListener('mousemove', mover);
      document.removeEventListener('mouseup', parar);
      document.body.classList.remove('chat-resizing');
    };

    document.body.classList.add('chat-resizing');
    document.addEventListener('mousemove', mover);
    document.addEventListener('mouseup', parar);
  };

  const lista = escolhendoPessoa && organization?.id && !(duasColunas && listaColapsada) ? (
    <NovaConversaDireta
      organizationId={organization.id}
      meuAuthId={user?.id ?? null}
      onEscolher={handleEscolherPessoa}
      onCancelar={() => setEscolhendoPessoa(false)}
    />
  ) : (
    <ChatConversationList
      grupos={grupos}
      termo={termo}
      onTermoChange={setTermo}
      chaveAtiva={chaveAtiva}
      onSelecionar={handleSelecionar}
      onNovaConversa={() => setEscolhendoPessoa(true)}
      carregando={loading}
      colapsada={duasColunas && listaColapsada}
      onAlternarColapso={onAlternarLista}
      permiteColapsar={duasColunas}
    />
  );

  // Mobile: sem espaço para duas colunas, a conversa cobre a lista e volta
  // pelo botão do header do ChatRoom.
  if (!duasColunas) {
    return (
      <div className="chat-interface single">
        {currentRoom ? <ChatRoom onBack={() => clearCurrentRoom()} /> : lista}
      </div>
    );
  }

  return (
    <div className="chat-interface split" ref={splitRef}>
      <div
        className="chat-interface-list"
        style={{ width: listaColapsada ? undefined : larguraDaLista }}
      >
        {lista}
      </div>

      {!listaColapsada && (
        <div
          className="chat-interface-divider"
          onMouseDown={iniciarResizeDaLista}
          role="separator"
          aria-orientation="vertical"
          title="Arraste para redimensionar a lista"
        />
      )}

      <div className="chat-interface-panel">
        {currentRoom ? (
          <ChatRoom />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-2 px-6 text-center">
            <MessageCircle className="w-8 h-8 text-gray-300" />
            <p className="text-sm font-medium text-gray-600">Selecione uma conversa</p>
            <p className="text-xs">
              Escolha um projeto ou uma conversa privada na lista para ver as mensagens.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatInterface;
